<template>
  <section class="hero">
    <!-- Background with gradient -->
    <div class="hero__background"></div>
    
    <div class="hero__container">
      <div class="hero__content">
        <!-- Main Content -->
        <div class="hero__text">
          <h1 class="hero__title">
            Deja de caer en <span class="hero__highlight">Spam</span>. 
            Envía correos que sí llegan al <span class="hero__highlight">Inbox</span>.
          </h1>
          
          <p class="hero__subtitle">
            El 15% de tus correos nunca llega a su destino por falta de validación. 
            No pierdas más dinero enviando mensajes a buzones muertos o trampas de spam.
          </p>
          
          <!-- Feature List -->
          <ul class="hero__features">
            <li class="hero__feature">
              <i class="pi pi-check-circle"></i>
              <span><strong>Validación 99%</strong> — Tu reputación de dominio intocable</span>
            </li>
            <li class="hero__feature">
              <i class="pi pi-check-circle"></i>
              <span><strong>Flujos automatizados</strong> — Vende mientras duermes</span>
            </li>
            <li class="hero__feature">
              <i class="pi pi-check-circle"></i>
              <span><strong>IA integrada</strong> — Campañas profesionales en segundos</span>
            </li>
          </ul>
          
          <!-- CTA Buttons -->
          <div class="hero__actions">
            <Button 
              label="Comenzar Gratis" 
              class="p-button-lg hero__cta-primary"
              icon="pi pi-arrow-right"
              iconPos="right"
              @click="emit('cta-click')"
            />
            <!-- <Button 
              label="Ver Demo" 
              class="p-button-lg p-button-outlined hero__cta-secondary"
              icon="pi pi-play"
            /> -->
          </div>
          
          <!-- Trust Indicators -->
          <div class="hero__trust">
            <div class="hero__trust-item">
              <i class="pi pi-shield"></i>
              <span>Sin tarjeta requerida</span>
            </div>
            <div class="hero__trust-item">
              <i class="pi pi-clock"></i>
              <span>Setup en 5 minutos</span>
            </div>
            <div class="hero__trust-item">
              <i class="pi pi-bolt"></i>
              <span>IA integrada</span>
            </div>
          </div>
        </div>
        
        <!-- Hero Visual -->
        <div class="hero__visual">
          <div class="hero__dashboard">
            <!-- Main Dashboard Card -->
            <div class="hero__dashboard-main">
              <div class="hero__dashboard-header">
                <div class="hero__dashboard-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
                <span class="hero__dashboard-title">ThunderLabs Dashboard</span>
              </div>
              <div class="hero__dashboard-content">
                <!-- Stats Row -->
                <div class="hero__stats-row">
                  <div class="hero__stat-card hero__stat-card--success">
                    <span class="hero__stat-value">99.2%</span>
                    <span class="hero__stat-label">Entregabilidad</span>
                  </div>
                  <div class="hero__stat-card">
                    <span class="hero__stat-value">45.8%</span>
                    <span class="hero__stat-label">Tasa de Apertura</span>
                  </div>
                  <div class="hero__stat-card">
                    <span class="hero__stat-value">12.4%</span>
                    <span class="hero__stat-label">Click Rate</span>
                  </div>
                </div>
                
                <!-- Email Preview -->
                <div class="hero__email-preview">
                  <div class="hero__email-skeleton"></div>
                  <div class="hero__email-skeleton hero__email-skeleton--short"></div>
                  <div class="hero__email-skeleton hero__email-skeleton--medium"></div>
                </div>
              </div>
            </div>
            
            <!-- Floating Feature Cards -->
            <div class="hero__floating-card hero__floating-card--validate">
              <div class="hero__floating-icon hero__floating-icon--success">
                <i class="pi pi-check"></i>
              </div>
              <div class="hero__floating-content">
                <span class="hero__floating-title">Validación SMTP</span>
                <span class="hero__floating-subtitle">Correo verificado ✓</span>
              </div>
            </div>
            
            <div class="hero__floating-card hero__floating-card--journey">
              <div class="hero__floating-icon hero__floating-icon--primary">
                <i class="pi pi-sitemap"></i>
              </div>
              <div class="hero__floating-content">
                <span class="hero__floating-title">Journey Builder</span>
                <span class="hero__floating-subtitle">3 flujos activos</span>
              </div>
            </div>
            
            <div class="hero__floating-card hero__floating-card--ai">
              <div class="hero__floating-icon hero__floating-icon--accent">
                <i class="pi pi-sparkles"></i>
              </div>
              <div class="hero__floating-content">
                <span class="hero__floating-title">AI Content</span>
                <span class="hero__floating-subtitle">Generando...</span>
              </div>
              <ProgressBar :value="65" :showValue="false" class="hero__ai-progress" />
            </div>
            
            <div class="hero__floating-card hero__floating-card--email">
              <div class="hero__floating-icon hero__floating-icon--email">
                <i class="pi pi-envelope"></i>
              </div>
              <div class="hero__floating-content">
                <span class="hero__floating-title">Email Marketing</span>
                <span class="hero__floating-subtitle">2,450 enviados hoy</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Soft Fade Divider -->
    <div class="hero__soft-fade"></div>
  </section>

</template>

<script setup>
import Button from 'primevue/button'
import ProgressBar from 'primevue/progressbar'

const emit = defineEmits(['cta-click'])
</script>

<style lang="scss" scoped>
// Import design system variables
@import '@/design-system/variables';

// ===========================================
// HERO SECTION
// ===========================================

.hero {
  position: relative;
  min-height: 100vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: $tl-space-16 0 $tl-space-24;
  
  @media (max-width: 768px) {
    padding: $tl-space-8 0 $tl-space-16;
    min-height: auto;
  }
}

// Background Gradient
.hero__background {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    #2B2B2B 0%,
    #505050 30%,
    #F28E4F 70%,
    #FF8000 100%
  );
  z-index: -1;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 128, 0, 0.15) 0%, transparent 40%);
  }
}

// Container
.hero__container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 $tl-page-padding;
  
  @media (max-width: 768px) {
    padding: 0 $tl-space-4;
  }
}

// Content Grid
.hero__content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: $tl-space-12;
  align-items: center;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: $tl-space-8;
    text-align: center;
  }
}

// ===========================================
// TEXT CONTENT
// ===========================================

.hero__text {
  @media (max-width: 1024px) {
    order: 1;
  }
}

.hero__title {
  font-size: clamp(2.5rem, 5vw, 3.75rem);
  font-weight: $tl-font-weight-bold;
  line-height: 1.1;
  color: $tl-white;
  margin-bottom: $tl-space-6;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
}

.hero__highlight {
  position: relative;
  color: $tl-action;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0.1em;
    left: 0;
    right: 0;
    height: 0.15em;
    background: rgba($tl-action, 0.4);
    border-radius: $tl-radius-full;
  }
}

.hero__subtitle {
  font-size: $tl-font-size-xl;
  line-height: $tl-line-height-relaxed;
  color: rgba($tl-white, 0.9);
  margin-bottom: $tl-space-8;
  max-width: 540px;
  
  @media (max-width: 1024px) {
    margin-left: auto;
    margin-right: auto;
  }
  
  @media (max-width: 768px) {
    font-size: $tl-font-size-base;
  }
}

// Feature List
.hero__features {
  list-style: none;
  padding: 0;
  margin: 0 0 $tl-space-8;
  display: flex;
  flex-direction: column;
  gap: $tl-space-3;
  
  @media (max-width: 1024px) {
    align-items: center;
  }
}

.hero__feature {
  display: flex;
  align-items: center;
  gap: $tl-space-3;
  color: $tl-white;
  font-size: $tl-font-size-base;
  
  i {
    color: #7fc8a9;
    font-size: $tl-font-size-xl;
  }
  
  strong {
    color: $tl-white;
  }
}

// CTA Buttons
.hero__actions {
  display: flex;
  gap: $tl-space-4;
  margin-bottom: $tl-space-8;
  
  @media (max-width: 1024px) {
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
}

.hero__cta-primary {
  background: $tl-action !important;
  border-color: $tl-action !important;
  font-weight: $tl-font-weight-semibold;
  padding: $tl-button-padding-lg;
  box-shadow: $tl-shadow-action-glow;
  
  &:hover {
    background: $tl-action-600 !important;
    border-color: $tl-action-600 !important;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba($tl-action, 0.35);
  }
}

.hero__cta-secondary {
  background: rgba($tl-white, 0.1) !important;
  border-color: rgba($tl-white, 0.5) !important;
  color: $tl-white !important;
  
  &:hover {
    background: rgba($tl-white, 0.2) !important;
    border-color: $tl-white !important;
  }
}

// Trust Indicators
.hero__trust {
  display: flex;
  gap: $tl-space-6;
  flex-wrap: wrap;
  
  @media (max-width: 1024px) {
    justify-content: center;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: $tl-space-3;
  }
}

.hero__trust-item {
  display: flex;
  align-items: center;
  gap: $tl-space-2;
  color: rgba($tl-white, 0.8);
  font-size: $tl-font-size-sm;
  
  i {
    color: rgba($tl-white, 0.6);
  }
}

// ===========================================
// VISUAL / DASHBOARD
// ===========================================

.hero__visual {
  position: relative;
  
  @media (max-width: 1024px) {
    order: 2;
    max-width: 600px;
    margin: 0 auto;
    width: 100%;
  }
}

.hero__dashboard {
  position: relative;
  perspective: 1000px;
}

.hero__dashboard-main {
  background: $tl-white;
  border-radius: $tl-radius-xl;
  box-shadow: $tl-shadow-2xl;
  overflow: hidden;
  transform: rotateY(-5deg) rotateX(5deg);
  transition: transform $tl-duration-slow $tl-easing-ease-out;
  
  &:hover {
    transform: rotateY(-2deg) rotateX(2deg);
  }
  
  @media (max-width: 1024px) {
    transform: none;
    
    &:hover {
      transform: none;
    }
  }
}

.hero__dashboard-header {
  background: $tl-gray-100;
  padding: $tl-space-3 $tl-space-4;
  display: flex;
  align-items: center;
  gap: $tl-space-3;
  border-bottom: 1px solid $tl-border-color-light;
}

.hero__dashboard-dots {
  display: flex;
  gap: $tl-space-1-5;
  
  span {
    width: 12px;
    height: 12px;
    border-radius: $tl-radius-full;
    
    &:nth-child(1) { background: #ff5f57; }
    &:nth-child(2) { background: #ffbd2e; }
    &:nth-child(3) { background: #28ca42; }
  }
}

.hero__dashboard-title {
  font-size: $tl-font-size-sm;
  font-weight: $tl-font-weight-medium;
  color: $tl-gray-600;
}

.hero__dashboard-content {
  padding: $tl-space-5;
}

// Stats Row
.hero__stats-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: $tl-space-3;
  margin-bottom: $tl-space-5;
}

.hero__stat-card {
  background: $tl-gray-50;
  border-radius: $tl-radius-md;
  padding: $tl-space-4;
  text-align: center;
  
  &--success {
    background: $tl-success-light;
    
    .hero__stat-value {
      color: $tl-success;
    }
  }
}

.hero__stat-value {
  display: block;
  font-size: $tl-font-size-2xl;
  font-weight: $tl-font-weight-bold;
  color: $tl-jet;
  margin-bottom: $tl-space-1;
  
  @media (max-width: 480px) {
    font-size: $tl-font-size-lg;
  }
}

.hero__stat-label {
  font-size: $tl-font-size-xs;
  color: $tl-gray-600;
}

// Email Preview Skeleton
.hero__email-preview {
  display: flex;
  flex-direction: column;
  gap: $tl-space-2;
}

.hero__email-skeleton {
  height: 12px;
  background: linear-gradient(90deg, $tl-gray-200, $tl-gray-100, $tl-gray-200);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: $tl-radius-sm;
  
  &--short {
    width: 60%;
  }
  
  &--medium {
    width: 80%;
  }
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

// Floating Cards
.hero__floating-card {
  position: absolute;
  background: $tl-white;
  border-radius: $tl-radius-lg;
  padding: $tl-space-3 $tl-space-4;
  box-shadow: $tl-shadow-lg;
  display: flex;
  align-items: center;
  gap: $tl-space-3;
  animation: float 3s ease-in-out infinite;
  
  &--validate {
    top: 20%;
    left: -20%;
    animation-delay: 0s;
    
    @media (max-width: 1024px) {
      top: -10%;
      left: 5%;
    }
  }
  
  &--journey {
    top: 50%;
    right: -15%;
    animation-delay: 1s;
    
    @media (max-width: 1024px) {
      top: 40%;
      right: -5%;
    }
  }
  
  &--ai {
    bottom: 10%;
    left: -10%;
    flex-direction: column;
    align-items: flex-start;
    animation-delay: 2s;
    
    @media (max-width: 1024px) {
      bottom: -5%;
      left: 10%;
    }
  }
  
  &--email {
    top: 10%;
    right: -18%;
    animation-delay: 0.5s;
    
    @media (max-width: 1024px) {
      top: 5%;
      right: -5%;
    }
  }
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

.hero__floating-icon {
  width: 40px;
  height: 40px;
  border-radius: $tl-radius-md;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &--success {
    background: $tl-success-light;
    color: $tl-success;
  }
  
  &--primary {
    background: $tl-primary-100;
    color: $tl-primary;
  }
  
  &--accent {
    background: rgba($tl-action, 0.15);
    color: $tl-action;
  }
  
  &--email {
    background: rgba(#3b82f6, 0.15);
    color: #3b82f6;
  }
  
  i {
    font-size: $tl-font-size-lg;
  }
}

.hero__floating-content {
  display: flex;
  flex-direction: column;
}

.hero__floating-title {
  font-size: $tl-font-size-sm;
  font-weight: $tl-font-weight-semibold;
  color: $tl-jet;
}

.hero__floating-subtitle {
  font-size: $tl-font-size-xs;
  color: $tl-gray-500;
}

.hero__ai-progress {
  width: 100%;
  height: 6px;
  margin-top: $tl-space-2;
  
  :deep(.p-progressbar-value) {
    background: linear-gradient(90deg, $tl-primary, $tl-action);
  }
}

// Soft Fade Divider
.hero__soft-fade {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 200px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba($tl-gray-50, 0.1) 20%,
    rgba($tl-gray-50, 0.3) 40%,
    rgba($tl-gray-50, 0.6) 60%,
    rgba($tl-gray-50, 0.85) 80%,
    $tl-gray-50 100%
  );
  pointer-events: none;
  
  @media (max-width: 768px) {
    height: 120px;
  }
}
</style>
